var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Module } from "../module";
import { AvatarLoadedMessage, LoadAvatarMessage } from "../../messages/rendering/RendererMessages";
import { ModuleId, AskMessage, AvatarStyle } from "flavr-common";
import { HeadMeshProvider } from "./HeadMeshProvider";
import { MonitorMeshProvider } from "./MonitorMeshProvider";
import { ShoulderMeshProvider } from "./ShoulderMeshProvider";
/**
 * Resolves avatar mesh requests into concrete head and shoulder objects so other modules can instantiate peer and player visuals without owning the loading logic.
 *
 * Messages:
 * - `LoadAvatarMessage` - Describes the requested avatar style and user identity for a mesh load request.
 * - `AvatarLoadedMessage` - Is sent back after the required avatar meshes have been prepared.
 */
export class AvatarLoaderModule extends Module {
    /**
     * Subscribes to `LoadAvatarMessage` requests and resolves each one via `handleLoadAvatarMessage`.
     */
    constructor() {
        super(ModuleId.AVATAR_LOADER);
        this.headMeshProvider = new HeadMeshProvider();
        this.monitorMeshProvider = new MonitorMeshProvider();
        this.shoulderMeshProvider = new ShoulderMeshProvider();
        this.connection.subscribe([LoadAvatarMessage.typestatic], (message) => __awaiter(this, void 0, void 0, function* () {
            yield this.handleLoadAvatarMessage(message);
        }));
    }
    /**
     * Keeps the standard module life-cycle contract even though this module finishes its setup in the constructor.
     */
    init() { }
    handleLoadAvatarMessage(message) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const msg = AskMessage.is(message) ? message.message : message;
                const askId = AskMessage.is(message) ? message.askId : undefined;
                if (!LoadAvatarMessage.is(msg)) {
                    throw new Error(`AvatarLoaderModule got unexpected message! ${JSON.stringify(message)}`);
                }
                const shoulderMesh = yield this.shoulderMeshProvider.getShoulderMesh(this.connection);
                const headMesh = yield this.getHeadObjectMesh(msg);
                const avatarLoadedMessage = new AvatarLoadedMessage(msg.username, headMesh, shoulderMesh);
                this.connection.send(avatarLoadedMessage, askId);
            }
            catch (error) {
                console.error(error);
            }
        });
    }
    getHeadObjectMesh(msg) {
        return __awaiter(this, void 0, void 0, function* () {
            switch (msg.avatarStyle) {
                case AvatarStyle.Head:
                    return yield this.headMeshProvider.getHeadMesh(this.connection);
                case AvatarStyle.Monitor:
                    return yield this.monitorMeshProvider.getMonitorObject(this.connection);
                default:
                    console.warn(`cannot yet handle AvatarStyle. ${msg.avatarStyle}`);
                    return yield this.monitorMeshProvider.getMonitorObject(this.connection);
            }
        });
    }
}
